import { 
    API_ENDPOINTS, 
    customToast, 
    DataColumnInterface, 
    DataTable, 
    DateFormats, 
    formatDateFns, 
    FormInput, 
    FormInputType, 
    Icon, 
    Icons, 
    Modal, 
    PageLayout, 
    Permissions, 
    replaceKeyWithValue, 
    route, 
    StringHelpers, 
    useAuthorisationContext, 
    useDisabledContext, 
    useForm, 
    useLangContext, 
    useQueries,
    Variants
} from "carrier-fe";
import { useEffect, useMemo, useRef, useState } from "react";
import { Product, ProductModalType } from "../../types/product";
import { DropdownItemProps } from "carrier-fe/lib/components/Controls/Dropdown/DropdownItem/DropdownItem";
import axios from "axios";
import { PRODUCT_TABLES } from "../../Constants";

const defaultData = {
    product_child_id: '',
    type_id: '',
    product_line_id: '',
    model: '',
};

function ProductsList() {
    const { fields, crud } = useLangContext();
    const { disabled, setDisabled } = useDisabledContext();
    const { hasPermissionAccess } = useAuthorisationContext();
    const [activeModal, setActiveModal] = useState<ProductModalType>(null);
    const [selectedProductParent, setSelectedProductParent] = useState<string>('');
    const [selectedProduct, setSelectedProduct] = useState<Partial<Product>>();
    const [clearFilters, setClearFilters] = useState<boolean>(false);
    const [isEdit, setIsEdit] = useState<boolean>();
    const [pendingFilters, setPendingFilters] = useState<{ 
        filter: string; value: string 
    }[]>([]);

    const pendingFiltersObj: {
        child?: string,
        type?: string,
        line?: string
    } = useMemo(() => (
        Object.fromEntries(
            pendingFilters.map(pendingFilter => [
                pendingFilter.filter, pendingFilter.value
            ])
        )
    ), [pendingFilters]);

    const pendingFiltersRef = useRef(pendingFiltersObj);

    const submitProduct = async (productData: typeof defaultData) => {
        return isEdit && selectedProduct?.id
        ? axios.patch(
            route(
                API_ENDPOINTS.ADMIN.PRODUCT.UPDATE,
                { product: selectedProduct?.id }
            ),
            productData
        ) 
        : axios.post(
            route(API_ENDPOINTS.ADMIN.PRODUCT.STORE),
            productData
        );
    };

    const deleteProduct = async () => {
        setDisabled(true);
        try {
            const response = await axios.delete(
                route(
                    API_ENDPOINTS.ADMIN.PRODUCT.DELETE,
                    { product: selectedProduct?.id || '' }
                )
            )
            customToast({
                title: response?.data?.message
                    || 'Success. Data for product(s) has been deleted.',
                variant: Variants.Success
            });
            setIsSuccess(true);
        } catch (error: any) {
            customToast({
                title: error?.response?.data?.message
                || 'Error deleting product',
                variant: Variants.Danger
            });
        } finally {
            setDisabled(false);
            closeModal();
        }
    };

    const restoreProduct = async () => {
        setDisabled(true);
        try {
            const response = await axios.patch(
                route(
                    API_ENDPOINTS.ADMIN.PRODUCT.RESTORE,
                    { product: selectedProduct?.id || '' }
                )
            )
            customToast({
                title: response?.data?.message
                    || 'Success. Data for product(s) has been restored.',
                variant: Variants.Success
            });
            setIsSuccess(true);
        } catch (error: any) {
            customToast({
                title: error?.response?.data?.message
                || 'Error restoring product',
                variant: Variants.Danger
            });
        } finally {
            setDisabled(false);
            closeModal();
        }
    };

    const { 
        store, 
        setValues, 
        Submit, 
        isSuccess,
        setIsSuccess,
        errors,
        setErrors
    } = useForm(submitProduct, defaultData);

    const isFormIncomplete = useMemo(() => {
        return Object.values(store).some(filled => !filled);
    }, [store]);

    const getChildrenRouteParams = () => ({
        ...selectedProductParent && {
            product_parent_id: selectedProductParent
        }
    });

    const getTypesRouteParams = () => ({
        ...store?.product_child_id && {
            product_child_id: store.product_child_id
        },
        ...pendingFiltersObj.child
            && activeModal !== 'create_update' && {
            product_child_id: pendingFiltersObj.child
        }
    });

    const getLinesRouteParams = () => ({
        ...store?.product_child_id && {
            product_child_id: store.product_child_id
        },
        ...store?.type_id && {
            product_type_id: store.type_id
        },
        ...pendingFiltersObj.child
            && activeModal !== 'create_update' && {
            product_child_id: pendingFiltersObj.child
        },
        ...pendingFiltersObj.type
            && activeModal !== 'create_update' && {
            product_type_id: pendingFiltersObj.type
        }
    });

    const {
        data: productData, 
        refetch
    } = useQueries([
        {
            key: PRODUCT_TABLES.PARENTS,
            config: {
                method: 'GET',
                url: route(API_ENDPOINTS.GENERIC.SELECT.PRODUCT.PARENTS),
            },
        },
        {
            key: PRODUCT_TABLES.CHILDREN,
            config: {
                method: 'GET',
                url: route(API_ENDPOINTS.GENERIC.SELECT.PRODUCT.CHILDREN, 
                    getChildrenRouteParams()
                ),
            },
        },
        {
            key: PRODUCT_TABLES.TYPES,
            config: {
                method: 'GET',
                url: route(API_ENDPOINTS.GENERIC.SELECT.PRODUCT.TYPES, 
                    getTypesRouteParams()
                ),
            },
        },
        {
            key: PRODUCT_TABLES.LINES,
            config: {
                method: 'GET',
                url: route(API_ENDPOINTS.GENERIC.SELECT.PRODUCT.LINES, 
                    getLinesRouteParams()
                ),
            },
        },
    ]);

    const productSelectOptions = useMemo(() => ({
        parents: productData?.product_parents?.data ?? [],
        children: productData?.product_children?.data ?? [],
        types: productData?.product_types?.data ?? [],
        lines: productData?.product_lines?.data ?? [],
    }), [productData]);
    
    const selectedProductOptionsRef = useRef({
        parent: selectedProductParent,
        child: store?.product_child_id,
        type: store?.type_id
    });
    
    const openModal = (modalType: ProductModalType) => {
        setActiveModal(modalType);
        setIsSuccess(false);
    };
    const closeModal = () => {
        setActiveModal(null);
        setSelectedProductParent('');
        setSelectedProduct({});
        setValues(defaultData);
        setClearFilters(false);
        setErrors({});
    };

    useEffect(() => {
        if (!isEdit) {
            const prev = selectedProductOptionsRef.current;
    
            if (selectedProductParent !== prev.parent) {
                refetch([PRODUCT_TABLES.CHILDREN]);
            } else if (store?.product_child_id !== prev.child) {
                refetch([PRODUCT_TABLES.TYPES]);
            } else if (store?.type_id !== prev.type) {
                refetch([PRODUCT_TABLES.LINES]);
            }
    
            selectedProductOptionsRef.current = {
                parent: selectedProductParent,
                child: store?.product_child_id,
                type: store?.type_id
            };
        } else {
            refetch([
                PRODUCT_TABLES.CHILDREN, 
                PRODUCT_TABLES.TYPES, 
                PRODUCT_TABLES.LINES
            ]);
        }
    }, [
        isEdit, 
        selectedProductParent, 
        store?.product_child_id, 
        store?.type_id
    ]);

    useEffect(() => {
        const {
            child,
            type,
        } = pendingFiltersObj;

        const prev = pendingFiltersRef.current;

        if ((child !== prev.child)) {
            refetch([
                PRODUCT_TABLES.TYPES,
                PRODUCT_TABLES.LINES
            ]);
        } else if ((type !== prev.type)) {
            refetch([PRODUCT_TABLES.LINES]);
        } 

        pendingFiltersRef.current = Object.fromEntries(
            pendingFilters.map(pendingFilter => [
                pendingFilter.filter, pendingFilter.value
            ])
        );

    } ,[pendingFiltersObj]);

    useEffect(() => {
        if (isSuccess) closeModal();
    }, [isSuccess]);

    const title = useMemo(
        () => StringHelpers.title(
            replaceKeyWithValue(
                crud?.pages?.index?.title || ':model List',
                'model',
                crud?.models?.product
                    || 'Product'
            )
        ),
        [crud]
    );

    const handleOptionSelect = (action: unknown, selectedRow: Product) => {
        const {
            product_child: { 
                id: productChildId, 
                product_parent_id: productParentId 
            },
            product_type: { id: productTypeId },
            product_line: { id: productLineId },
            model: productModel 
        } = selectedRow;

        const actions: Record<string, () => void> = {
            update: () => {
                setIsEdit(true);
                setSelectedProductParent(productParentId);
                setValues({
                    product_child_id: productChildId,
                    type_id: productTypeId,
                    product_line_id: productLineId,
                    model: productModel
                });
                setSelectedProduct(selectedRow);
                openModal('create_update');
            },
            delete: () => {
                setSelectedProduct(selectedRow);
                openModal('delete');
            },
            restore: () => {
                setSelectedProduct(selectedRow);
                openModal('restore');
            },
        };

        if (typeof action === 'string' && actions[action]) {
            actions[action]();
        }
    };

    const colMapping = (row: Product): DataColumnInterface[] => {
        const {
            product_child: { product_child },
            product_type: { type },
            product_line: { product_line },
            model,
            created_at,
            deleted_at,
            updated_at,
        } = row;

        return [
            {
                id: 'child',
                label: crud?.models?.product_child || 'Product Child',
                value: product_child,
                sortable: true,
            },
            {
                id: 'type',
                label: crud?.models?.product_type || 'Product Type',
                value: type,
                sortable: true
            },
            {
                id: 'line',
                label: crud?.models?.product_line || 'Product Line', 
                value: product_line,
                sortable: true
            },
            {
                id: 'model',
                label: fields?.model || 'Model',
                value: model,
                sortable: true
            },
            {
                id: 'created_at',
                label: StringHelpers.title(
                    fields?.created_at
                    || 'created'
                ),
                value: formatDateFns(
                    created_at 
                    || '-', 
                    DateFormats.DATETIME_SHORT
                ),
                sortable: true,
            },
            {
                id: 'updated_at',
                label: StringHelpers.title(
                    fields?.updated_at 
                    || 'updated'
                ),
                value: formatDateFns(
                    updated_at
                    || '-',
                    DateFormats.DATETIME_SHORT
                ),
                sortable: true,
                visibleCondition: () => !deleted_at
            },
            {
                id: 'deleted_at',
                label: StringHelpers.title(
                    fields?.deleted_at 
                    || 'deleted'
                ),
                value: formatDateFns(
                    deleted_at
                    || '-',
                    DateFormats.DATETIME_SHORT
                ),
                sortable: true,
                visibleCondition: () => !!deleted_at
            },
            {
                id: 'actions',
                label: StringHelpers.title(
                    fields?.actions 
                        || 'Actions'
                ),
                value: <FormInput
                    type='select'
                    label={StringHelpers.title(
                        fields?.actions 
                        || 'Action'
                    )}
                    placeholder={StringHelpers.title(
                        fields?.select 
                        || 'Select'
                    )}
                    name='actions'
                    value=''
                    onChange={(value) => {
                        if (!!value && typeof value === 'string') {
                            handleOptionSelect(value, row);
                        }
                    }}
                    options={[
                        ...!deleted_at ? [{
                            label: crud?.buttons?.edit?.default
                                || 'Update',
                            value: 'update',
                            disabled: !hasPermissionAccess(Permissions.PRODUCT_UPDATE)
                        }, {
                            label: crud?.buttons?.delete?.default
                                || 'Delete',
                            value: 'delete',
                            disabled: !hasPermissionAccess(Permissions.PRODUCT_DELETE)
                        }] : [{
                            label: crud?.buttons?.restore?.default
                                || 'Restore',
                            value: 'restore',
                            disabled: !hasPermissionAccess(Permissions.PRODUCT_RESTORE)
                        }],
                    ]}
                /> 
            }
        ];
    };

    const filters: FormInputType[] = [
        {
            name: 'child',
            label: crud?.models?.product_child || 'Product Child',
            type: 'searchable-select',
            value: '',
            options: productSelectOptions?.children,
            disabled: !!pendingFiltersObj.type,
            placeholder: !!pendingFiltersObj.type ? 
                replaceKeyWithValue(
                    crud?.placeholders?.clear
                        || 'Clear :model to select',
                    'model',
                    StringHelpers.lower(
                        crud?.table_columns?.type
                        || 'Type'
                    ),
                )
                : StringHelpers.title(
                    fields?.select
                    || 'select'
                ),
        },
        {
            name: 'type',
            label: crud?.models?.product_type || 'Product Type',
            type: 'searchable-select',
            value: '',
            options: productSelectOptions?.types,
            disabled: !!pendingFiltersObj.line,
            placeholder: StringHelpers.title(
                    fields?.select
                    || 'select'
                ),
        },
        ...pendingFiltersObj.type ? [{
            name: 'line',
            label: crud?.models?.product_line || 'Product Line',
            type: 'searchable-select',
            value: '',
            options: productSelectOptions?.lines,
            placeholder: StringHelpers.title(
                    fields?.select
                    || 'select'
                ),
        }] : [],
    ];

    const dropdownItems: DropdownItemProps[] = [
        ...hasPermissionAccess(Permissions.PRODUCT_CREATE) ? [{
            label: StringHelpers.title(crud?.buttons?.create?.default || 'Create'),
            onClick: () => {
                setIsEdit(false);
                setClearFilters(true);
                openModal('create_update');
            },
            icon: <Icon icon={Icons.ADD} />,
        }] : []
    ]; 

    const modalDescription = () => {
        return (
            <ul>
                <li>
                    <strong>
                        {crud?.models?.product_child 
                            || 'Product Child'
                        }
                    </strong>: {selectedProduct?.product_child?.product_child}
                </li>
                <li>
                    <strong>
                        {crud?.models?.product_type 
                            || 'Product Type'
                        }
                    </strong>: {selectedProduct?.product_type?.type}
                </li>
                <li>
                    <strong>
                        {crud?.models?.product_line 
                            || 'Product Line'
                        }
                    </strong>: {selectedProduct?.product_line?.product_line}
                </li>
            </ul>
        )
    };

    return (
        <PageLayout title={title || 'Product List'}>
            <DataTable 
                routeEndpoint={route(API_ENDPOINTS.ADMIN.PRODUCT.INDEX)}
                colMapping={colMapping}
                items={dropdownItems}
                refreshOnChange={
                    isSuccess ? '' : undefined
                }
                incrementPaginationButtons
                variant={Variants.Info}
                canViewArchived
                filters={filters}
                setParentPendingFilterParams={setPendingFilters}
                parentClearFilters={clearFilters}
                disableFilterBadges
            />

            <Modal
                open={activeModal === 'create_update'}
                onClose={closeModal}
                title={StringHelpers.title(
                    replaceKeyWithValue(
                        isEdit ? 
                        crud?.pages?.edit?.title
                            || 'Edit :model'
                        : crud?.pages?.create?.title
                            || 'Create :model',
                        'model',
                        crud?.models?.product
                            || 'Product'
                    )
                )}
                overriddenConfirmButton={
                    <Submit
                        defaultLabel={
                            isEdit ?
                            crud?.buttons?.edit?.default
                                || 'Update'
                            : crud?.buttons?.create?.default 
                                || 'Create'
                        }
                        loadingLabel={
                            isEdit ?
                            crud?.buttons?.edit?.submitting
                                || 'Updating...'
                            : crud?.buttons?.create?.submitting
                                || 'Creating...'
                        }
                        disabled={disabled || isFormIncomplete}
                    />
                }
                disabled={disabled}
                variant={Variants.Primary}
            >
                {<FormInput
                    type='searchable-select'
                    name='product_parent'
                    label={
                        crud?.models?.product_parent
                        || 'Product Parent'
                    }
                    placeholder={
                        StringHelpers.title(
                            fields?.please_select
                            || 'please select'
                        )
                    }
                    value={selectedProductParent}
                    onChange={(v) => {
                        setSelectedProductParent(v as string);
                        setValues(defaultData);
                    }}
                    options={productSelectOptions.parents}
                    disabled={disabled || isEdit}
                />}

                {selectedProductParent && <FormInput
                    type='searchable-select'
                    name='product_child'
                    label={
                        crud?.models?.product_child
                        || 'Product Child'
                    }
                    placeholder={
                        StringHelpers.title(
                            fields?.please_select
                            || 'please select'
                        )
                    }
                    value={store?.product_child_id}
                    onChange={(value) => 
                        setValues({
                            product_child_id: value as string,
                            ...value !== store?.product_child_id && {
                                type_id: '',
                                product_line_id: ''
                            }
                        })
                    }
                    options={productSelectOptions.children}
                    disabled={disabled}
                    errorMessages={errors?.product_child_id}
                />}

                {store?.product_child_id && <FormInput 
                    type='searchable-select'
                    name='product_type'
                    label={
                        crud?.models?.product_type
                        || 'Product Type'
                    }
                    placeholder={
                        StringHelpers.title(
                            fields?.please_select
                            || 'please select'
                        )
                    }
                    value={store?.type_id}
                    onChange={(value) =>
                        setValues({
                            type_id: value as string,
                            ...value !== store?.type_id && {
                                product_line_id: ''
                            }
                        })
                    }
                    options={productSelectOptions.types}
                    disabled={disabled}
                    errorMessages={errors?.type_id}
                />}

                {store?.type_id && <FormInput 
                    type='searchable-select'
                    name='product_line'
                    label={
                        crud?.models?.product_line
                        || 'Product Line'
                    }
                    placeholder={
                        StringHelpers.title(
                            fields?.please_select
                            || 'please select'
                        )
                    }
                    value={store?.product_line_id}
                    onChange={(v) => setValues({
                        product_line_id: v as string,
                    })}
                    options={productSelectOptions.lines}
                    disabled={disabled}
                    errorMessages={errors?.product_line_id}
                />}

                {store?.product_line_id && <FormInput
                    type='text'
                    name='model'
                    label={
                        fields?.model
                        || 'Model'
                    }
                    value={store?.model}
                    onChange={(v) => setValues({
                        model: v as string
                    })}
                    disabled={disabled}
                    errorMessages={errors?.model}
                />}
            </Modal>

            <Modal
                open={activeModal === 'delete'}
                onClose={closeModal}
                title={replaceKeyWithValue(
                        crud?.modals?.delete?.title
                            || 'Delete :name?',
                        'name',
                        crud?.models?.product
                            || 'Product'
                    )
                }
                onConfirm={deleteProduct}
                confirmText={disabled
                    ? crud?.buttons?.delete?.submitting
                        || 'Deleting...'
                    : crud?.butons?.delete?.default
                        || 'Delete'
                }
                variant={Variants.Primary}
            >
                {crud?.modals?.delete?.description?.map((sentence: string, index: number) => 
                    <p key={index}>
                        {replaceKeyWithValue(
                            sentence,
                            'name',
                            selectedProduct?.model || ''
                        )}
                    </p>
                )}

                {modalDescription()}
            </Modal>

            <Modal
                open={activeModal === 'restore'}
                onClose={closeModal}
                title={replaceKeyWithValue(
                        crud?.modals?.restore?.title
                            || 'Restore :name?',
                        'name',
                        crud?.models?.product
                            || 'Product'
                    )
                }
                onConfirm={restoreProduct}
                confirmText={disabled
                    ? crud?.buttons?.restore?.submitting
                        || 'Restoring...'
                    : crud?.butons?.restore?.default
                        || 'Restore'
                }
                variant={Variants.Primary}
            >
                {crud?.modals?.restore?.description?.map((sentence: string, index: number) => 
                    <p key={index}>
                        {replaceKeyWithValue(
                            sentence,
                            'name',
                            selectedProduct?.model || ''
                        )}
                    </p>
                )}

                {modalDescription()}
            </Modal>
        </PageLayout>
    )
};

export default ProductsList;